import { QueryOperation, SearchQuery } from "../../code/search-query/search-query";
import {
  buildSearchQuery,
  comboboxValue,
  dateRangeValue,
  QueryFieldMapping,
} from "../../code/search-query/query-mapper";
import { DataColumn } from "../../controls/Data.default";
import { InputObject } from "../../controls/default";
import {
  ReportAppraisalVerdict,
  WellbeingReportStatus,
} from "../../repository/data.default";
import { Tag, FormInstance } from "antd";
import { Dayjs } from "dayjs";
import { DynamicSchema } from "../../controls/DynamicForm.default";
import {
  createSearchRequest,
  createScopedFilterRequest,
} from "../../hooks/useSearchRequest";
import {
  getCompanies,
  getCompanyEmployees,
} from "../../repository/data.repository";
import { createStatusColorMap } from "../../utils/colors";

export interface ReportsViewForm {
  readonly company: InputObject;
  readonly user: InputObject;
  readonly status: InputObject<WellbeingReportStatus>;
  readonly createdOn: Dayjs[];
  readonly createdByUserUserName: string;
  readonly updatedOn: Dayjs[];
  readonly updatedByUserUserName: string;
}

// Status → tag colour, shared by the grid column and the Manage dialog.
export const reportStatusColor = createStatusColorMap<string>({
  [WellbeingReportStatus.Draft]: "default",
  [WellbeingReportStatus.Submitted]: "gold",
  [WellbeingReportStatus.Appraised]: "green",
});

// One-line guidance per status for the Manage dialog banner.
export const reportGuidance = (status?: string): string => {
  switch (status) {
    case WellbeingReportStatus.Draft:
      return "Draft — add reviewers and PDF(s), then submit it for appraisal.";
    case WellbeingReportStatus.Submitted:
      return "Submitted — awaiting the reviewers' appraisal. It is read-only until they respond.";
    case WellbeingReportStatus.Appraised:
      return "Appraised — all reviewers have approved this report.";
    default:
      return "";
  }
};

// Reviewer verdict → tag colour (Pending stays on the default fallback).
export const reportVerdictColor = createStatusColorMap<string>({
  [ReportAppraisalVerdict.Approved]: "green",
  [ReportAppraisalVerdict.ChangesRequested]: "red",
});

export const reportsViewColumns: DataColumn[] = [
  {
    title: "Company",
    key: "companyName",
    width: 192,
    sort: true,
    defaultSort: "asc",
  },
  {
    title: "Subject",
    key: "reportingForUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Department",
    key: "departmentName",
    width: 160,
    sort: true,
  },
  {
    title: "Asset",
    key: "assetName",
    width: 160,
    sort: true,
  },
  {
    title: "Status",
    key: "status",
    width: 140,
    sort: true,
    transform: (value: string) => (
      <Tag color={reportStatusColor(value)}>{value}</Tag>
    ),
  },
  {
    title: "Created on",
    key: "createdOn",
    width: 192,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Created By",
    key: "createdByUserUserName",
    width: 192,
    sort: true,
  },
  {
    title: "Summary",
    key: "summary",
    width: 192,
    sort: true,
  },

  {
    title: "Files",
    key: "filesCount",
    width: 192,
    sort: true,
    type: "badge",
  },
  {
    title: "Reviewers",
    key: "reviewersCount",
    width: 192,
    sort: true,
    type: "badge",
  },
  {
    title: "Updated On",
    key: "updatedOn",
    width: 192,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Updated By",
    key: "updatedByUserUserName",
    width: 192,
    sort: true,
  },
];

const reportsQueryMappings: QueryFieldMapping[] = [
  { formField: "company", queryField: "companyUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "user", queryField: "reportingForUserUid", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "status", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "createdByUserUserName", operation: QueryOperation.Contains },
  { formField: "updatedOn", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "updatedByUserUserName", operation: QueryOperation.Contains },
];

export const mapReportsViewQuery = (values: ReportsViewForm): SearchQuery =>
  buildSearchQuery(values, reportsQueryMappings);

// Factory so the User (report subject) dropdown scopes to the selected Company (soft scope; `dependsOn` clears
// a stale selection when the company changes).
export const reportsSearchSchema = (form: FormInstance<any>): DynamicSchema => ({
  groups: [
    {
      controls: [
        {
          type: "combobox",
          name: "company",
          label: "Company",
          request: createSearchRequest(getCompanies, "name"),
        },
        {
          type: "combobox",
          name: "user",
          label: "User Name",
          dependsOn: ["company"],
          request: createScopedFilterRequest(
            getCompanyEmployees,
            "firstName",
            () => [["companyUid", form.getFieldValue("company")?.value]],
            (d) => d.userName,
          ),
        },
        {
          type: "combobox",
          name: "status",
          label: "Status",
          request: () =>
            Promise.resolve(
              Object.values(WellbeingReportStatus).map((s) => ({
                display: s,
                value: s,
              })),
            ),
        },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
        { type: "textbox", name: "createdByUserUserName", label: "Created By" },
        { type: "rangetimepicker", name: "updatedOn", label: "Updated On" },
        { type: "textbox", name: "updatedByUserUserName", label: "Updated By" },
      ],
    },
  ],
});
